/**
 * ShellSuperAdmin — layout do painel do Super Admin (F11).
 *
 * Réplica do includes/sidebar_super_admin.html: barra roxa com coroa, o
 * seletor "Acessar Prefeitura" e os atalhos administrativos.
 *
 * Duas diferenças de fundo em relação ao Jinja:
 *  - as entradas "Todos os Usuários", "Estatísticas" e "Configurações"
 *    redirecionavam de volta para o painel (eram abas disfarçadas de
 *    página); aqui são abas de verdade, com rota própria;
 *  - todas as ferramentas do painel são rota da SPA: não sobrou link de
 *    página inteira para o Jinja.
 */

import {
  faChartBar,
  faChartLine,
  faCity,
  faCrown,
  faDatabase,
  faFileInvoiceDollar,
  faGaugeHigh,
  faHospitalAlt,
  faRightFromBracket,
  faShieldAlt,
  faSpinner,
  faUsers,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router'
import { logout, me } from '../api/auth'
import { ApiError } from '../api/http'
import { assumirPrefeitura, painelSuperAdmin } from '../api/superAdmin'
import { useToast } from './Toast'

const ABAS = [
  { rota: 'prefeituras', rotulo: 'Prefeituras', icone: faCity, cor: 'text-green-300' },
  { rota: 'usuarios', rotulo: 'Todos os Usuários', icone: faUsers, cor: 'text-blue-300' },
  { rota: 'estatisticas', rotulo: 'Estatísticas', icone: faChartBar, cor: 'text-pink-300' },
  { rota: 'auditorias', rotulo: 'Auditoria de Preços', icone: faShieldAlt, cor: 'text-orange-300' },
  { rota: 'pca', rotulo: 'Conversor PCA', icone: faFileInvoiceDollar, cor: 'text-emerald-300' },
  { rota: 'rag', rotulo: 'Base de Conhecimento', icone: faDatabase, cor: 'text-cyan-300' },
  { rota: 'tabela-sus', rotulo: 'Tabela SUS', icone: faHospitalAlt, cor: 'text-teal-300' },
  { rota: 'tokens', rotulo: 'Uso de Tokens', icone: faChartLine, cor: 'text-amber-300' },
]

export function ShellSuperAdmin() {
  const localizacao = useLocation()
  const navegar = useNavigate()
  const clienteQuery = useQueryClient()
  const { notificar } = useToast()

  const { data: sessao, error, isLoading } = useQuery({
    queryKey: ['sessao'],
    queryFn: me,
    retry: false,
    staleTime: 5 * 60 * 1000,
  })
  const { data: painel } = useQuery({
    queryKey: ['super-admin', 'painel'],
    queryFn: painelSuperAdmin,
    enabled: sessao?.usuario.papel === 'super_admin',
  })

  const assumir = useMutation({
    mutationFn: (tenantId: number) => assumirPrefeitura(tenantId),
    onSuccess: (prefeitura) => {
      notificar('success', `Acessando ${prefeitura.municipio}…`)
      // troca de contexto vale para o app inteiro: recarrega fora do painel
      window.location.href = '/app/dashboard'
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 text-gray-500 dark:bg-dark-bg">
        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
        Carregando…
      </div>
    )
  }
  if (error instanceof ApiError && error.codigo === 'NAO_AUTENTICADO') {
    return (
      <Navigate
        to={`/login?next=${encodeURIComponent(localizacao.pathname)}`}
        replace
      />
    )
  }
  if (error || !sessao) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 text-red-600 dark:bg-dark-bg">
        Erro ao carregar a sessão. Recarregue a página.
      </div>
    )
  }
  if (sessao.usuario.papel !== 'super_admin') {
    return <Navigate to="/dashboard" replace />
  }

  async function sair() {
    await logout()
    clienteQuery.removeQueries({ queryKey: ['sessao'] })
    navegar('/login')
  }

  const item =
    'flex items-center gap-4 rounded-lg px-4 py-3 transition-all duration-300'

  return (
    <div className="flex min-h-screen bg-slate-100 dark:bg-dark-bg">
      <nav className="sticky top-0 hidden h-screen w-72 flex-shrink-0 flex-col overflow-hidden bg-gradient-to-b from-purple-800 via-purple-900 to-indigo-950 text-white md:flex">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute left-1/2 top-0 h-96 w-96 -translate-x-1/2 rounded-full bg-yellow-500/5 blur-3xl" />
          <div className="absolute bottom-0 right-0 h-64 w-64 rounded-full bg-purple-500/10 blur-3xl" />
        </div>

        <div className="relative shrink-0 border-b border-white/20 p-4 text-center">
          <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-yellow-400 to-yellow-300 shadow-lg">
            <FontAwesomeIcon icon={faCrown} className="text-3xl text-purple-900" />
          </div>
          <h1 className="text-xl font-bold text-white">Super Admin</h1>
          <p className="text-sm text-purple-200">LicitIA</p>
        </div>

        <div className="relative min-h-0 flex-1 overflow-y-auto p-4">
          <div className="mb-6 text-center">
            <h3 className="truncate font-semibold text-white">
              {sessao.usuario.nome ?? sessao.usuario.email}
            </h3>
            <p className="text-sm text-purple-200">Super Administrador</p>
          </div>

          <div className="space-y-2">
            <NavLink
              to="/super-admin/prefeituras"
              className={`${item} bg-white/10 hover:bg-white/20`}
            >
              <FontAwesomeIcon icon={faGaugeHigh} className="w-5 text-yellow-300" />
              <span>Dashboard</span>
            </NavLink>

            {ABAS.map((aba) => (
              <NavLink
                key={aba.rota}
                to={`/super-admin/${aba.rota}`}
                className={({ isActive }) =>
                  `${item} ${
                    isActive
                      ? 'border-l-4 border-yellow-400 bg-white/20'
                      : 'hover:bg-white/10'
                  }`
                }
              >
                <FontAwesomeIcon icon={aba.icone} className={`w-5 ${aba.cor}`} />
                <span>{aba.rotulo}</span>
              </NavLink>
            ))}

            <div className="rounded-lg border border-indigo-400/50 bg-gradient-to-r from-indigo-600/30 to-purple-600/30 p-4">
              <label
                htmlFor="prefeitura-ativa"
                className="mb-2 block text-xs font-semibold text-purple-200"
              >
                Acessar prefeitura
              </label>
              <select
                id="prefeitura-ativa"
                value={sessao.tenant_ativo ?? ''}
                onChange={(e) =>
                  e.target.value && assumir.mutate(Number(e.target.value))
                }
                disabled={assumir.isPending}
                className="w-full rounded-lg border border-white/30 bg-white/10 px-3 py-2 text-sm text-white focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400"
              >
                <option value="" className="text-slate-800">
                  Selecione…
                </option>
                {(painel?.prefeituras ?? [])
                  .filter((p) => p.ativa)
                  .map((p) => (
                    <option key={p.id} value={p.id} className="text-slate-800">
                      {p.municipio} — {p.orgao}
                    </option>
                  ))}
              </select>
              <p className="mt-2 text-xs text-purple-200">
                Escolha para entrar no sistema como aquela prefeitura.
              </p>
            </div>

          </div>
        </div>

        <div className="relative mt-auto shrink-0 space-y-2 border-t border-white/20 p-4">
          <Link
            to="/dashboard"
            className={`${item} bg-white/10 hover:bg-white/20`}
          >
            <FontAwesomeIcon icon={faGaugeHigh} className="w-5" />
            <span>Ir para o sistema</span>
          </Link>
          <button
            type="button"
            onClick={sair}
            className={`${item} w-full bg-red-500/50 hover:bg-red-500/80`}
          >
            <FontAwesomeIcon icon={faRightFromBracket} className="w-5" />
            <span>Sair</span>
          </button>
        </div>
      </nav>

      <main className="min-w-0 flex-1 p-4 md:p-8">
        <Outlet context={sessao} />
      </main>
    </div>
  )
}
